import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  BarChart2,
  Download,
  RefreshCcw,
  Search,
  Smartphone,
  Users,
} from "react-feather";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  clearOverallSummaryReport,
  fetchOverallSummaryReportThunk,
} from "../../../store/slices/zeroBillReportSlice";

import "./ReportPage.css";

const DEFAULT_PAGE_SIZE = 10;

// =====================================================
// DATE HELPERS
// =====================================================

const getTodayString = () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDefaultFromDate = () => {
  const date = new Date();

  date.setDate(date.getDate() - 29);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const calculateInclusiveDays = (fromDate, toDate) => {
  if (!fromDate || !toDate) {
    return 0;
  }

  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T00:00:00`);

  if (
    Number.isNaN(from.getTime()) ||
    Number.isNaN(to.getTime())
  ) {
    return 0;
  }

  return (
    Math.floor(
      (to.getTime() - from.getTime()) /
        (1000 * 60 * 60 * 24),
    ) + 1
  );
};

// =====================================================
// FORMAT HELPERS
// =====================================================

const formatAmount = (value) => {
  const number = Number(value || 0);

  return number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCount = (value) => {
  return Number(value || 0).toLocaleString("en-IN");
};

const displayValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "-";
  }

  return value;
};

const escapeHtml = (value) => {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// =====================================================
// COLUMN CONFIG
// UI = EXCEL = PDF
// =====================================================

const panchayathColumns = [
  {
    key: "sl_no",
    label: "Sl No.",
    excelWidth: 8,
    pdfWidth: "4%",
  },
  {
    key: "pan_name",
    label: "Panchayath",
    excelWidth: 24,
    pdfWidth: "10%",
    kannada: true,
  },
  {
    key: "dist_name",
    label: "District",
    excelWidth: 18,
    pdfWidth: "7%",
    kannada: true,
  },
  {
    key: "taluk_name",
    label: "Taluk",
    excelWidth: 18,
    pdfWidth: "7%",
    kannada: true,
  },
  {
    key: "total_houses",
    label: "Total Houses",
    excelWidth: 14,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "water_connections",
    label: "Water Conn.",
    excelWidth: 14,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "slrm_connections",
    label: "SLRM Conn.",
    excelWidth: 14,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "water_bills_generated",
    label: "Water Bills",
    excelWidth: 14,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "slrm_bills_generated",
    label: "SLRM Bills",
    excelWidth: 14,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "total_demand",
    label: "Total Demand",
    excelWidth: 16,
    pdfWidth: "6%",
    type: "amount",
  },
  {
    key: "total_collection",
    label: "Total Collection",
    excelWidth: 18,
    pdfWidth: "6%",
    type: "amount",
  },
  {
    key: "total_discount",
    label: "Total Discount",
    excelWidth: 16,
    pdfWidth: "6%",
    type: "amount",
  },
  {
    key: "total_balance",
    label: "Current Balance",
    excelWidth: 17,
    pdfWidth: "6%",
    type: "amount",
  },
  {
    key: "water_receipt_count",
    label: "Water Receipts",
    excelWidth: 16,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "slrm_receipt_count",
    label: "SLRM Receipts",
    excelWidth: 16,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "total_receipt_count",
    label: "Total Receipts",
    excelWidth: 16,
    pdfWidth: "5%",
    type: "count",
  },
  {
    key: "houses_not_billed",
    label: "Houses Not Billed",
    excelWidth: 18,
    pdfWidth: "6%",
    type: "count",
  },
  {
    key: "collection_percentage",
    label: "Collection %",
    excelWidth: 14,
    pdfWidth: "5%",
    type: "percent",
  },
  {
    key: "income_amount",
    label: "Income",
    excelWidth: 16,
    pdfWidth: "6%",
    type: "amount",
  },
  {
    key: "expense_amount",
    label: "Expense",
    excelWidth: 16,
    pdfWidth: "6%",
    type: "amount",
  },
  {
    key: "net_income",
    label: "Net Income",
    excelWidth: 16,
    pdfWidth: "6%",
    type: "amount",
  },
];

const agentColumns = [
  {
    key: "sl_no",
    label: "Sl No.",
    excelWidth: 8,
    pdfWidth: "5%",
  },
  {
    key: "agent_name",
    label: "Agent",
    excelWidth: 22,
    pdfWidth: "10%",
    kannada: true,
  },
  {
    key: "mobile",
    label: "Mobile",
    excelWidth: 16,
    pdfWidth: "8%",
  },
  {
    key: "pan_name",
    label: "Panchayath",
    excelWidth: 22,
    pdfWidth: "10%",
    kannada: true,
  },
  {
    key: "status",
    label: "Status",
    excelWidth: 12,
    pdfWidth: "6%",
  },
  {
    key: "water_bills_generated",
    label: "Water Bills",
    excelWidth: 14,
    pdfWidth: "6%",
    type: "count",
  },
  {
    key: "slrm_bills_generated",
    label: "SLRM Bills",
    excelWidth: 14,
    pdfWidth: "6%",
    type: "count",
  },
  {
    key: "total_bills_generated",
    label: "Total Bills",
    excelWidth: 14,
    pdfWidth: "6%",
    type: "count",
  },
  {
    key: "receipts_generated",
    label: "Receipts",
    excelWidth: 12,
    pdfWidth: "6%",
    type: "count",
  },
  {
    key: "water_demand",
    label: "Water Demand",
    excelWidth: 16,
    pdfWidth: "7%",
    type: "amount",
  },
  {
    key: "slrm_demand",
    label: "SLRM Demand",
    excelWidth: 16,
    pdfWidth: "7%",
    type: "amount",
  },
  {
    key: "water_collection",
    label: "Water Collection",
    excelWidth: 18,
    pdfWidth: "8%",
    type: "amount",
  },
  {
    key: "slrm_collection",
    label: "SLRM Collection",
    excelWidth: 18,
    pdfWidth: "8%",
    type: "amount",
  },
  {
    key: "total_collection",
    label: "Total Collection",
    excelWidth: 18,
    pdfWidth: "8%",
    type: "amount",
  },
  {
    key: "houses_billed",
    label: "Houses Billed",
    excelWidth: 16,
    pdfWidth: "7%",
    type: "count",
  },
  {
    key: "houses_collected",
    label: "Houses Collected",
    excelWidth: 18,
    pdfWidth: "7%",
    type: "count",
  },
  {
    key: "last_activity",
    label: "Last Activity",
    excelWidth: 22,
    pdfWidth: "10%",
  },
];

const officeColumns = [
  {
    key: "sl_no",
    label: "Sl No.",
    excelWidth: 8,
    pdfWidth: "5%",
  },
  {
    key: "username",
    label: "User Name",
    excelWidth: 20,
    pdfWidth: "10%",
    kannada: true,
  },
  {
    key: "role",
    label: "Role",
    excelWidth: 14,
    pdfWidth: "7%",
  },
  {
    key: "receipts_generated",
    label: "Receipts",
    excelWidth: 12,
    pdfWidth: "6%",
    type: "count",
  },
  {
    key: "houses_collected",
    label: "Houses Collected",
    excelWidth: 18,
    pdfWidth: "8%",
    type: "count",
  },
  {
    key: "water_collection",
    label: "Water Collection",
    excelWidth: 18,
    pdfWidth: "9%",
    type: "amount",
  },
  {
    key: "slrm_collection",
    label: "SLRM Collection",
    excelWidth: 18,
    pdfWidth: "9%",
    type: "amount",
  },
  {
    key: "total_collection",
    label: "Total Collection",
    excelWidth: 18,
    pdfWidth: "9%",
    type: "amount",
  },
  {
    key: "income_entries",
    label: "Income Entries",
    excelWidth: 16,
    pdfWidth: "7%",
    type: "count",
  },
  {
    key: "income_amount",
    label: "Income Amount",
    excelWidth: 17,
    pdfWidth: "8%",
    type: "amount",
  },
  {
    key: "expense_entries",
    label: "Expense Entries",
    excelWidth: 17,
    pdfWidth: "7%",
    type: "count",
  },
  {
    key: "expense_amount",
    label: "Expense Amount",
    excelWidth: 18,
    pdfWidth: "8%",
    type: "amount",
  },
  {
    key: "net_income",
    label: "Net Income",
    excelWidth: 16,
    pdfWidth: "8%",
    type: "amount",
  },
  {
    key: "last_activity",
    label: "Last Activity",
    excelWidth: 22,
    pdfWidth: "10%",
  },
];

// =====================================================
// COLUMN VALUE HELPERS
// =====================================================

const getRawColumnValue = (row, column, index) => {
  if (column.key === "sl_no") {
    return row?.sl_no ?? index + 1;
  }

  return row?.[column.key] ?? "";
};

const getDisplayColumnValue = (row, column, index) => {
  const value = getRawColumnValue(row, column, index);

  if (column.type === "count") {
    return formatCount(value);
  }

  if (column.type === "amount") {
    return `₹${formatAmount(value)}`;
  }

  if (column.type === "percent") {
    return `${formatAmount(value)}%`;
  }

  return displayValue(value);
};

const getExcelColumnValue = (row, column, index) => {
  const value = getRawColumnValue(row, column, index);

  if (column.type === "count") {
    return Number(value || 0);
  }

  if (
    column.type === "amount" ||
    column.type === "percent"
  ) {
    return Number(value || 0);
  }

  return value ?? "";
};

// =====================================================
// PDF HTML HELPERS
// =====================================================

const buildPdfSectionElement = ({
  title,
  columns,
  rows,
  periodText,
}) => {
  const wrapper = document.createElement("div");

  wrapper.style.position = "fixed";
  wrapper.style.left = "-20000px";
  wrapper.style.top = "0";
  wrapper.style.width = "1800px";
  wrapper.style.background = "#ffffff";
  wrapper.style.color = "#000000";
  wrapper.style.padding = "22px";
  wrapper.style.boxSizing = "border-box";
  wrapper.style.fontFamily =
    '"Noto Sans Kannada","Nirmala UI","Tunga",Arial,sans-serif';

  const titleEl = document.createElement("div");
  titleEl.textContent = title;
  titleEl.style.fontFamily = "Arial,sans-serif";
  titleEl.style.fontSize = "22px";
  titleEl.style.fontWeight = "700";
  titleEl.style.marginBottom = "5px";
  wrapper.appendChild(titleEl);

  const metaEl = document.createElement("div");
  metaEl.textContent =
    `${periodText} | Total Records: ${rows.length}`;
  metaEl.style.fontFamily = "Arial,sans-serif";
  metaEl.style.fontSize = "12px";
  metaEl.style.marginBottom = "12px";
  wrapper.appendChild(metaEl);

  const table = document.createElement("table");
  table.style.width = "100%";
  table.style.borderCollapse = "collapse";
  table.style.tableLayout = "fixed";

  const thead = document.createElement("thead");
  const headerRow = document.createElement("tr");

  columns.forEach((column) => {
    const th = document.createElement("th");
    th.textContent = column.label;
    th.style.width = column.pdfWidth || "auto";
    th.style.border = "1px solid #9ca3af";
    th.style.background = "#eeeeee";
    th.style.padding = "5px 3px";
    th.style.fontFamily = "Arial,sans-serif";
    th.style.fontSize = "9px";
    th.style.fontWeight = "700";
    th.style.lineHeight = "1.25";
    th.style.textAlign = "center";
    th.style.verticalAlign = "middle";
    th.style.wordBreak = "normal";
    th.style.overflowWrap = "break-word";
    headerRow.appendChild(th);
  });

  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");

  rows.forEach((row, index) => {
    const tr = document.createElement("tr");

    columns.forEach((column) => {
      const td = document.createElement("td");

      td.textContent = String(
        getDisplayColumnValue(row, column, index) ?? "",
      );

      td.style.border = "1px solid #d1d5db";
      td.style.padding = "4px 3px";
      td.style.fontFamily =
        '"Noto Sans Kannada","Nirmala UI","Tunga",Arial,sans-serif';
      td.style.fontSize = column.kannada ? "10px" : "9px";
      td.style.lineHeight = column.kannada ? "1.45" : "1.35";
      td.style.verticalAlign = "middle";
      td.style.whiteSpace = "normal";
      td.style.wordBreak = "normal";
      td.style.overflowWrap = "break-word";
      td.style.letterSpacing = "normal";
      td.style.wordSpacing = "normal";

      if (
        column.type === "count" ||
        column.type === "amount" ||
        column.type === "percent"
      ) {
        td.style.textAlign = "right";
      } else if (column.key === "sl_no") {
        td.style.textAlign = "center";
      } else {
        td.style.textAlign = "left";
      }

      tr.appendChild(td);
    });

    tbody.appendChild(tr);
  });

  table.appendChild(tbody);
  wrapper.appendChild(table);
  document.body.appendChild(wrapper);

  return wrapper;
};

const addCanvasToPdf = ({
  doc,
  canvas,
  addNewPage,
}) => {
  if (addNewPage) {
    doc.addPage();
  }

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const pageHeight =
    doc.internal.pageSize.getHeight();

  const imageData =
    canvas.toDataURL("image/png");

  const imageWidth = pageWidth - 24;
  const imageHeight =
    (canvas.height * imageWidth) /
    canvas.width;

  const usableHeight = pageHeight - 24;

  if (imageHeight <= usableHeight) {
    doc.addImage(
      imageData,
      "PNG",
      12,
      12,
      imageWidth,
      imageHeight,
      undefined,
      "FAST",
    );

    return;
  }

  const ratio =
    canvas.width / imageWidth;

  const sliceHeightPx =
    Math.floor(usableHeight * ratio);

  let offsetY = 0;
  let isFirstSlice = true;

  while (offsetY < canvas.height) {
    if (!isFirstSlice) {
      doc.addPage();
    }

    const currentSliceHeight =
      Math.min(
        sliceHeightPx,
        canvas.height - offsetY,
      );

    const sliceCanvas =
      document.createElement("canvas");

    sliceCanvas.width = canvas.width;
    sliceCanvas.height = currentSliceHeight;

    const context =
      sliceCanvas.getContext("2d");

    context.drawImage(
      canvas,
      0,
      offsetY,
      canvas.width,
      currentSliceHeight,
      0,
      0,
      canvas.width,
      currentSliceHeight,
    );

    const sliceImage =
      sliceCanvas.toDataURL("image/png");

    const slicePdfHeight =
      currentSliceHeight / ratio;

    doc.addImage(
      sliceImage,
      "PNG",
      12,
      12,
      imageWidth,
      slicePdfHeight,
      undefined,
      "FAST",
    );

    offsetY += currentSliceHeight;
    isFirstSlice = false;
  }
};

// =====================================================
// COMPONENT
// =====================================================

const OverallSummaryReportPage = () => {
  const dispatch = useDispatch();

  const {
    panchayathSummary = {
      rows: [],
      pagination: {},
    },

    agentSummary = {
      rows: [],
      pagination: {},
    },

    officeUserSummary = {
      rows: [],
      pagination: {},
    },

    loading = false,
    loaded = false,
    error = null,
  } = useSelector(
    (state) =>
      state.zeroBillReport?.overallSummary || {},
  );

  const [fromDate, setFromDate] = useState(
    getDefaultFromDate(),
  );

  const [toDate, setToDate] = useState(
    getTodayString(),
  );

  const [validationError, setValidationError] =
    useState("");

  const [panchayathSearch, setPanchayathSearch] =
    useState("");

  const [agentSearch, setAgentSearch] =
    useState("");

  const [officeSearch, setOfficeSearch] =
    useState("");

  const [panchayathPage, setPanchayathPage] =
    useState(1);

  const [panchayathLimit, setPanchayathLimit] =
    useState(DEFAULT_PAGE_SIZE);

  const [agentPage, setAgentPage] =
    useState(1);

  const [agentLimit, setAgentLimit] =
    useState(DEFAULT_PAGE_SIZE);

  const [officePage, setOfficePage] =
    useState(1);

  const [officeLimit, setOfficeLimit] =
    useState(DEFAULT_PAGE_SIZE);

  const [exportLoading, setExportLoading] =
    useState(false);

  // =====================================================
  // REDUX ROWS
  // =====================================================

  const panchayathRows = Array.isArray(
    panchayathSummary?.rows,
  )
    ? panchayathSummary.rows
    : [];

  const agentRows = Array.isArray(
    agentSummary?.rows,
  )
    ? agentSummary.rows
    : [];

  const officeRows = Array.isArray(
    officeUserSummary?.rows,
  )
    ? officeUserSummary.rows
    : [];

  const panchayathPagination =
    panchayathSummary?.pagination || {};

  const agentPagination =
    agentSummary?.pagination || {};

  const officePagination =
    officeUserSummary?.pagination || {};

  // =====================================================
  // VALIDATION
  // =====================================================

  const validateDates = useCallback(() => {
    if (!fromDate || !toDate) {
      return "From Date and To Date are required";
    }

    if (fromDate > toDate) {
      return "From Date cannot be greater than To Date";
    }

    const totalDays =
      calculateInclusiveDays(
        fromDate,
        toDate,
      );

    if (totalDays > 30) {
      return "Date range cannot exceed 30 days";
    }

    return "";
  }, [fromDate, toDate]);

  // =====================================================
  // LOAD REPORT
  // =====================================================

  const loadReport = useCallback(
    ({
      requestedPanchayathPage =
        panchayathPage,

      requestedPanchayathLimit =
        panchayathLimit,

      requestedAgentPage =
        agentPage,

      requestedAgentLimit =
        agentLimit,

      requestedOfficePage =
        officePage,

      requestedOfficeLimit =
        officeLimit,

      exportAll = false,
    } = {}) => {
      const dateError =
        validateDates();

      if (dateError) {
        setValidationError(
          dateError,
        );

        return;
      }

      setValidationError("");

      dispatch(
        fetchOverallSummaryReportThunk({
          from_date: fromDate,
          to_date: toDate,

          panchayath_page:
            requestedPanchayathPage,

          panchayath_limit:
            requestedPanchayathLimit,

          agent_page:
            requestedAgentPage,

          agent_limit:
            requestedAgentLimit,

          office_page:
            requestedOfficePage,

          office_limit:
            requestedOfficeLimit,

          export_all:
            exportAll,
        }),
      );
    },
    [
      dispatch,
      fromDate,
      toDate,
      panchayathPage,
      panchayathLimit,
      agentPage,
      agentLimit,
      officePage,
      officeLimit,
      validateDates,
    ],
  );

  // =====================================================
  // LOAD BUTTON
  // =====================================================

  const handleLoad = () => {
    setPanchayathPage(1);
    setAgentPage(1);
    setOfficePage(1);

    loadReport({
      requestedPanchayathPage: 1,
      requestedAgentPage: 1,
      requestedOfficePage: 1,
    });
  };

  // =====================================================
  // CLEAR
  // =====================================================

  const handleClear = () => {
    setFromDate(
      getDefaultFromDate(),
    );

    setToDate(
      getTodayString(),
    );

    setValidationError("");

    setPanchayathSearch("");
    setAgentSearch("");
    setOfficeSearch("");

    setPanchayathPage(1);
    setAgentPage(1);
    setOfficePage(1);

    setPanchayathLimit(
      DEFAULT_PAGE_SIZE,
    );

    setAgentLimit(
      DEFAULT_PAGE_SIZE,
    );

    setOfficeLimit(
      DEFAULT_PAGE_SIZE,
    );

    dispatch(
      clearOverallSummaryReport(),
    );
  };

  // =====================================================
  // PAGINATION CALLBACKS
  // =====================================================

  const handlePanchayathPageChange = (
    page,
  ) => {
    setPanchayathPage(page);

    loadReport({
      requestedPanchayathPage:
        page,
    });
  };

  const handleAgentPageChange = (
    page,
  ) => {
    setAgentPage(page);

    loadReport({
      requestedAgentPage:
        page,
    });
  };

  const handleOfficePageChange = (
    page,
  ) => {
    setOfficePage(page);

    loadReport({
      requestedOfficePage:
        page,
    });
  };

  const handlePanchayathLimitChange = (
    value,
  ) => {
    const limit =
      Number(value);

    setPanchayathLimit(
      limit,
    );

    setPanchayathPage(1);

    loadReport({
      requestedPanchayathPage:
        1,

      requestedPanchayathLimit:
        limit,
    });
  };

  const handleAgentLimitChange = (
    value,
  ) => {
    const limit =
      Number(value);

    setAgentLimit(limit);
    setAgentPage(1);

    loadReport({
      requestedAgentPage: 1,
      requestedAgentLimit:
        limit,
    });
  };

  const handleOfficeLimitChange = (
    value,
  ) => {
    const limit =
      Number(value);

    setOfficeLimit(limit);
    setOfficePage(1);

    loadReport({
      requestedOfficePage: 1,
      requestedOfficeLimit:
        limit,
    });
  };

  // =====================================================
  // SEARCH
  // =====================================================

  const filteredPanchayathRows =
    useMemo(() => {
      const query =
        panchayathSearch
          .trim()
          .toLowerCase();

      if (!query) {
        return panchayathRows;
      }

      return panchayathRows.filter(
        (row) => {
          const searchable =
            panchayathColumns
              .map((column) =>
                String(
                  getRawColumnValue(
                    row,
                    column,
                    0,
                  ) ?? "",
                ),
              )
              .join(" ")
              .toLowerCase();

          return searchable.includes(
            query,
          );
        },
      );
    }, [
      panchayathRows,
      panchayathSearch,
    ]);

  const filteredAgentRows =
    useMemo(() => {
      const query =
        agentSearch
          .trim()
          .toLowerCase();

      if (!query) {
        return agentRows;
      }

      return agentRows.filter(
        (row) => {
          const searchable =
            agentColumns
              .map((column) =>
                String(
                  getRawColumnValue(
                    row,
                    column,
                    0,
                  ) ?? "",
                ),
              )
              .join(" ")
              .toLowerCase();

          return searchable.includes(
            query,
          );
        },
      );
    }, [
      agentRows,
      agentSearch,
    ]);

  const filteredOfficeRows =
    useMemo(() => {
      const query =
        officeSearch
          .trim()
          .toLowerCase();

      if (!query) {
        return officeRows;
      }

      return officeRows.filter(
        (row) => {
          const searchable =
            officeColumns
              .map((column) =>
                String(
                  getRawColumnValue(
                    row,
                    column,
                    0,
                  ) ?? "",
                ),
              )
              .join(" ")
              .toLowerCase();

          return searchable.includes(
            query,
          );
        },
      );
    }, [
      officeRows,
      officeSearch,
    ]);

  // =====================================================
  // EXPORT DATA
  // =====================================================

  const getExportData = async () => {
    const dateError =
      validateDates();

    if (dateError) {
      setValidationError(
        dateError,
      );

      return null;
    }

    setValidationError("");

    try {
      const resultAction =
        await dispatch(
          fetchOverallSummaryReportThunk({
            from_date: fromDate,
            to_date: toDate,

            export_all: true,

            panchayath_page: 1,
            panchayath_limit:
              999999,

            agent_page: 1,
            agent_limit:
              999999,

            office_page: 1,
            office_limit:
              999999,
          }),
        );

      if (
        !fetchOverallSummaryReportThunk.fulfilled.match(
          resultAction,
        )
      ) {
        return null;
      }

      return resultAction.payload;
    } catch (exportError) {
      console.error(
        "Overall report export failed:",
        exportError,
      );

      return null;
    }
  };

  // =====================================================
  // EXCEL EXPORT
  // UI = EXCEL
  // =====================================================

  const createExcelSheet = (
    rows,
    columns,
  ) => {
    const data = rows.map(
      (row, index) => {
        const exportRow = {};

        columns.forEach(
          (column) => {
            exportRow[
              column.label
            ] =
              getExcelColumnValue(
                row,
                column,
                index,
              );
          },
        );

        return exportRow;
      },
    );

    const sheet =
      XLSX.utils.json_to_sheet(
        data,
      );

    sheet["!cols"] =
      columns.map(
        (column) => ({
          wch:
            column.excelWidth ||
            15,
        }),
      );

    return sheet;
  };

  const exportExcel = async () => {
    if (exportLoading) {
      return;
    }

    try {
      setExportLoading(true);

      const exportData =
        await getExportData();

      if (!exportData) {
        return;
      }

      const panRows =
        exportData
          ?.panchayathSummary
          ?.rows || [];

      const agentExportRows =
        exportData
          ?.agentSummary
          ?.rows || [];

      const officeExportRows =
        exportData
          ?.officeUserSummary
          ?.rows || [];

      const workbook =
        XLSX.utils.book_new();

      const panchayathSheet =
        createExcelSheet(
          panRows,
          panchayathColumns,
        );

      const agentSheet =
        createExcelSheet(
          agentExportRows,
          agentColumns,
        );

      const officeSheet =
        createExcelSheet(
          officeExportRows,
          officeColumns,
        );

      XLSX.utils.book_append_sheet(
        workbook,
        panchayathSheet,
        "Panchayath Summary",
      );

      XLSX.utils.book_append_sheet(
        workbook,
        agentSheet,
        "Agent Summary",
      );

      XLSX.utils.book_append_sheet(
        workbook,
        officeSheet,
        "Office User Summary",
      );

      XLSX.writeFile(
        workbook,
        `overall_summary_${fromDate}_${toDate}.xlsx`,
      );
    } catch (exportError) {
      console.error(
        "Excel export failed:",
        exportError,
      );
    } finally {
      setExportLoading(false);
    }
  };

  // =====================================================
  // PDF EXPORT
  // DIRECT DOWNLOAD
  // Browser renders Kannada -> html2canvas -> jsPDF
  // =====================================================

  const exportPDF = async () => {
    if (exportLoading) {
      return;
    }

    const createdElements = [];

    try {
      setExportLoading(true);

      const exportData =
        await getExportData();

      if (!exportData) {
        return;
      }

      const panRows =
        exportData
          ?.panchayathSummary
          ?.rows || [];

      const agentExportRows =
        exportData
          ?.agentSummary
          ?.rows || [];

      const officeExportRows =
        exportData
          ?.officeUserSummary
          ?.rows || [];

      if (
        !panRows.length &&
        !agentExportRows.length &&
        !officeExportRows.length
      ) {
        return;
      }

      if (
        document.fonts &&
        document.fonts.ready
      ) {
        await document.fonts.ready;
      }

      const periodText =
        `Period: ${fromDate} to ${toDate}`;

      const sections = [
        {
          title:
            "Panchayath Summary",
          columns:
            panchayathColumns,
          rows: panRows,
        },
        {
          title:
            "Agent-wise Report",
          columns:
            agentColumns,
          rows:
            agentExportRows,
        },
        {
          title:
            "Office User-wise Report",
          columns:
            officeColumns,
          rows:
            officeExportRows,
        },
      ];

      const doc = new jsPDF(
        "l",
        "pt",
        "a3",
      );

      let sectionIndex = 0;

      for (
        const section of sections
      ) {
        if (!section.rows.length) {
          continue;
        }

        const element =
          buildPdfSectionElement({
            title: section.title,
            columns:
              section.columns,
            rows:
              section.rows,
            periodText,
          });

        createdElements.push(
          element,
        );

        await new Promise(
          (resolve) =>
            requestAnimationFrame(
              () =>
                requestAnimationFrame(
                  resolve,
                ),
            ),
        );

        const canvas =
          await html2canvas(
            element,
            {
              scale: 2,
              useCORS: true,
              backgroundColor:
                "#ffffff",
              logging: false,
            },
          );

        addCanvasToPdf({
          doc,
          canvas,
          addNewPage:
            sectionIndex > 0,
        });

        sectionIndex += 1;
      }

      doc.save(
        `overall_summary_${fromDate}_${toDate}.pdf`,
      );
    } catch (exportError) {
      console.error(
        "PDF export failed:",
        exportError,
      );
    } finally {
      createdElements.forEach(
        (element) => {
          if (
            element &&
            element.parentNode
          ) {
            element.parentNode.removeChild(
              element,
            );
          }
        },
      );

      setExportLoading(false);
    }
  };

  // =====================================================
  // PAGINATION
  // =====================================================

  const renderPagination = ({
    currentPage,
    totalPages,
    onPageChange,
  }) => {
    const pages =
      Math.max(
        1,
        Number(
          totalPages || 1,
        ),
      );

    const page =
      Math.min(
        Math.max(
          1,
          Number(
            currentPage || 1,
          ),
        ),
        pages,
      );

    if (pages <= 1) {
      return null;
    }

    const items = [];
    const maxButtons = 5;

    let start =
      Math.max(
        1,
        page -
          Math.floor(
            maxButtons / 2,
          ),
      );

    let end =
      Math.min(
        pages,
        start +
          maxButtons -
          1,
      );

    start =
      Math.max(
        1,
        end -
          maxButtons +
          1,
      );

    items.push(
      <Pagination.Prev
        key="previous"
        disabled={
          page === 1 ||
          loading
        }
        onClick={() =>
          onPageChange(
            Math.max(
              1,
              page - 1,
            ),
          )
        }
      />,
    );

    if (start > 1) {
      items.push(
        <Pagination.Item
          key={1}
          disabled={loading}
          onClick={() =>
            onPageChange(1)
          }
        >
          1
        </Pagination.Item>,
      );

      if (start > 2) {
        items.push(
          <Pagination.Ellipsis
            key="start-ellipsis"
            disabled
          />,
        );
      }
    }

    for (
      let pageNumber =
        start;
      pageNumber <= end;
      pageNumber += 1
    ) {
      items.push(
        <Pagination.Item
          key={pageNumber}
          active={
            pageNumber ===
            page
          }
          disabled={loading}
          onClick={() =>
            onPageChange(
              pageNumber,
            )
          }
        >
          {pageNumber}
        </Pagination.Item>,
      );
    }

    if (end < pages) {
      if (
        end <
        pages - 1
      ) {
        items.push(
          <Pagination.Ellipsis
            key="end-ellipsis"
            disabled
          />,
        );
      }

      items.push(
        <Pagination.Item
          key={pages}
          disabled={loading}
          onClick={() =>
            onPageChange(
              pages,
            )
          }
        >
          {pages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={
          page === pages ||
          loading
        }
        onClick={() =>
          onPageChange(
            Math.min(
              pages,
              page + 1,
            ),
          )
        }
      />,
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  const renderPaginationBar = ({
    pagination,
    page,
    limit,
    onPageChange,
    onLimitChange,
  }) => {
    const totalPages =
      Math.max(
        1,
        Number(
          pagination
            ?.total_pages || 1,
        ),
      );

    const total =
      Number(
        pagination?.total || 0,
      );

    return (
      <div className="report-pagination-bar">

        <div className="report-page-size">
          <span>
            Rows:
          </span>

          <Form.Select
            value={limit}
            onChange={(event) =>
              onLimitChange(
                event.target.value,
              )
            }
            className="page-size-select"
            disabled={loading}
          >
            {[
              10,
              20,
              50,
              100,
            ].map(
              (size) => (
                <option
                  key={size}
                  value={size}
                >
                  {size}
                </option>
              ),
            )}
          </Form.Select>
        </div>

        <div className="report-pagination">
          {renderPagination({
            currentPage: page,
            totalPages,
            onPageChange,
          })}
        </div>

        <div className="report-page-info">
          Page {page} /{" "}
          {totalPages}

          <span className="ms-2">
            (
            {formatCount(
              total,
            )}{" "}
            records)
          </span>
        </div>

      </div>
    );
  };

  // =====================================================
  // COMMON TABLE RENDERERS
  // =====================================================

  const renderLoadingRow = (
    colSpan,
  ) => (
    <tr>
      <td
        colSpan={colSpan}
        className="text-center py-4"
      >
        <Spinner
          animation="border"
          size="sm"
          className="me-2"
        />

        Loading...
      </td>
    </tr>
  );

  const renderEmptyRow = (
    colSpan,
    message,
  ) => (
    <tr>
      <td
        colSpan={colSpan}
        className="text-center py-5"
      >
        <div className="fw-semibold">
          {message}
        </div>

        <div className="text-muted small mt-1">
          Select a date range
          and click Load.
        </div>
      </td>
    </tr>
  );

  const renderTableHeader = (
    columns,
  ) => (
    <thead>
      <tr>
        {columns.map(
          (column) => (
            <th
              key={
                column.key
              }
            >
              {column.label}
            </th>
          ),
        )}
      </tr>
    </thead>
  );

  const renderTableRow = (
    row,
    columns,
    index,
    keyPrefix,
  ) => (
    <tr
      key={`${keyPrefix}-${index}`}
    >
      {columns.map(
        (column) => (
          <td
            key={
              column.key
            }
            className={
              [
                "pan_name",
                "agent_name",
                "username",
              ].includes(
                column.key,
              )
                ? "fw-semibold"
                : ""
            }
          >
            {column.type ===
            "amount" ? (
              <>
                ₹
                {formatAmount(
                  getRawColumnValue(
                    row,
                    column,
                    index,
                  ),
                )}
              </>
            ) : column.type ===
              "count" ? (
              formatCount(
                getRawColumnValue(
                  row,
                  column,
                  index,
                ),
              )
            ) : column.type ===
              "percent" ? (
              <Badge
                bg="light"
                text="dark"
                className="border"
              >
                {formatAmount(
                  getRawColumnValue(
                    row,
                    column,
                    index,
                  ),
                )}
                %
              </Badge>
            ) : column.key ===
              "status" ? (
              <Badge
                bg="light"
                text="dark"
                className="border text-capitalize"
              >
                {displayValue(
                  getRawColumnValue(
                    row,
                    column,
                    index,
                  ),
                )}
              </Badge>
            ) : column.key ===
              "role" ? (
              <Badge
                bg="light"
                text="dark"
                className="border text-capitalize"
              >
                {displayValue(
                  getRawColumnValue(
                    row,
                    column,
                    index,
                  ),
                )}
              </Badge>
            ) : (
              displayValue(
                getRawColumnValue(
                  row,
                  column,
                  index,
                ),
              )
            )}
          </td>
        ),
      )}
    </tr>
  );

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="report-page">

      <Card className="report-card">

        <Card.Header className="report-card-header">

          <div className="report-header-row">

            <div>

              <div className="report-title">
                Overall Summary Report
              </div>

              <div className="text-muted small mt-1">
                Panchayath, mobile
                agent and office user
                activity summary
              </div>

            </div>

          </div>

        </Card.Header>

        <Card.Body>

          {validationError ? (
            <Alert variant="warning">
              {validationError}
            </Alert>
          ) : null}

          {error ? (
            <Alert variant="danger">
              {error}
            </Alert>
          ) : null}

          {/* ========================================= */}
          {/* FILTERS                                   */}
          {/* ========================================= */}

          <div className="report-filter-box">

            <Row className="g-3">

              <Col
                xl={3}
                lg={4}
                md={6}
              >

                <Form.Label className="report-label">
                  From Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={fromDate}
                  max={
                    toDate ||
                    getTodayString()
                  }
                  onChange={(event) => {
                    setFromDate(
                      event.target
                        .value,
                    );

                    setValidationError(
                      "",
                    );
                  }}
                />

              </Col>

              <Col
                xl={3}
                lg={4}
                md={6}
              >

                <Form.Label className="report-label">
                  To Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={toDate}
                  min={
                    fromDate ||
                    undefined
                  }
                  max={
                    getTodayString()
                  }
                  onChange={(event) => {
                    setToDate(
                      event.target
                        .value,
                    );

                    setValidationError(
                      "",
                    );
                  }}
                />

              </Col>

              <Col
                xl={6}
                lg={4}
                md={12}
                className="d-flex align-items-end"
              >

                <div className="report-filter-actions">

                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={
                      handleLoad
                    }
                    disabled={
                      loading ||
                      exportLoading
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={
                      handleClear
                    }
                    disabled={
                      loading ||
                      exportLoading
                    }
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />

                    Clear
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-primary"
                    onClick={
                      exportExcel
                    }
                    disabled={
                      loading ||
                      exportLoading ||
                      (!panchayathRows.length &&
                        !agentRows.length &&
                        !officeRows.length)
                    }
                  >
                    {exportLoading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                    ) : (
                      <Download
                        size={16}
                        className="me-2"
                      />
                    )}

                    Excel
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-danger"
                    onClick={
                      exportPDF
                    }
                    disabled={
                      loading ||
                      exportLoading ||
                      (!panchayathRows.length &&
                        !agentRows.length &&
                        !officeRows.length)
                    }
                  >
                    {exportLoading ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                    ) : (
                      <Download
                        size={16}
                        className="me-2"
                      />
                    )}

                    PDF
                  </Button>

                </div>

              </Col>

            </Row>

            <div className="text-muted small mt-2">
              Maximum selectable
              date range is 30 days.
            </div>

          </div>

          {/* ========================================= */}
          {/* PANCHAYATH SUMMARY                        */}
          {/* ========================================= */}

          <div className="report-table-toolbar mt-4">

            <div className="report-table-title">

              <span className="d-inline-flex align-items-center gap-2">
                <BarChart2 size={18} />

                Panchayath Summary
              </span>

              <span className="report-count ms-2">
                (
                {formatCount(
                  panchayathPagination
                    ?.total,
                )}{" "}
                records)
              </span>

            </div>

            <div className="report-table-right">

              <InputGroup className="report-search">

                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={
                    panchayathSearch
                  }
                  onChange={(event) =>
                    setPanchayathSearch(
                      event.target
                        .value,
                    )
                  }
                  placeholder="Search Panchayath / district / taluk..."
                />

              </InputGroup>

            </div>

          </div>

          <div className="report-table-wrap">

            <div className="report-table-scroll">

              <Table
                hover
                className="mb-0 report-table"
              >

                {renderTableHeader(
                  panchayathColumns,
                )}

                <tbody>

                  {loading &&
                  !loaded ? (
                    renderLoadingRow(
                      panchayathColumns.length,
                    )
                  ) : filteredPanchayathRows.length ? (
                    filteredPanchayathRows.map(
                      (
                        row,
                        index,
                      ) =>
                        renderTableRow(
                          row,
                          panchayathColumns,
                          index,
                          `pan-${
                            row.pan_id ||
                            index
                          }`,
                        ),
                    )
                  ) : (
                    renderEmptyRow(
                      panchayathColumns.length,
                      "No Panchayath summary found",
                    )
                  )}

                </tbody>

              </Table>

            </div>

            {renderPaginationBar({
              pagination:
                panchayathPagination,

              page:
                panchayathPage,

              limit:
                panchayathLimit,

              onPageChange:
                handlePanchayathPageChange,

              onLimitChange:
                handlePanchayathLimitChange,
            })}

          </div>

          {/* ========================================= */}
          {/* AGENT SUMMARY                             */}
          {/* ========================================= */}

          <div className="report-table-toolbar mt-4">

            <div className="report-table-title">

              <span className="d-inline-flex align-items-center gap-2">
                <Smartphone size={18} />

                Agent-wise Report
              </span>

              <span className="report-count ms-2">
                (
                {formatCount(
                  agentPagination
                    ?.total,
                )}{" "}
                records)
              </span>

            </div>

            <div className="report-table-right">

              <InputGroup className="report-search">

                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={
                    agentSearch
                  }
                  onChange={(event) =>
                    setAgentSearch(
                      event.target
                        .value,
                    )
                  }
                  placeholder="Search agent / mobile / Panchayath..."
                />

              </InputGroup>

            </div>

          </div>

          <div className="report-table-wrap">

            <div className="report-table-scroll">

              <Table
                hover
                className="mb-0 report-table"
              >

                {renderTableHeader(
                  agentColumns,
                )}

                <tbody>

                  {loading &&
                  !loaded ? (
                    renderLoadingRow(
                      agentColumns.length,
                    )
                  ) : filteredAgentRows.length ? (
                    filteredAgentRows.map(
                      (
                        row,
                        index,
                      ) =>
                        renderTableRow(
                          row,
                          agentColumns,
                          index,
                          `agent-${
                            row.pan_id ||
                            ""
                          }-${
                            row.agent_id ||
                            index
                          }`,
                        ),
                    )
                  ) : (
                    renderEmptyRow(
                      agentColumns.length,
                      "No agent activity found",
                    )
                  )}

                </tbody>

              </Table>

            </div>

            {renderPaginationBar({
              pagination:
                agentPagination,

              page:
                agentPage,

              limit:
                agentLimit,

              onPageChange:
                handleAgentPageChange,

              onLimitChange:
                handleAgentLimitChange,
            })}

          </div>

          {/* ========================================= */}
          {/* OFFICE USER SUMMARY                       */}
          {/* ========================================= */}

          <div className="report-table-toolbar mt-4">

            <div className="report-table-title">

              <span className="d-inline-flex align-items-center gap-2">
                <Users size={18} />

                Office User-wise
                Report
              </span>

              <span className="report-count ms-2">
                (
                {formatCount(
                  officePagination
                    ?.total,
                )}{" "}
                records)
              </span>

            </div>

            <div className="report-table-right">

              <InputGroup className="report-search">

                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={
                    officeSearch
                  }
                  onChange={(event) =>
                    setOfficeSearch(
                      event.target
                        .value,
                    )
                  }
                  placeholder="Search user / role..."
                />

              </InputGroup>

            </div>

          </div>

          <div className="report-table-wrap">

            <div className="report-table-scroll">

              <Table
                hover
                className="mb-0 report-table"
              >

                {renderTableHeader(
                  officeColumns,
                )}

                <tbody>

                  {loading &&
                  !loaded ? (
                    renderLoadingRow(
                      officeColumns.length,
                    )
                  ) : filteredOfficeRows.length ? (
                    filteredOfficeRows.map(
                      (
                        row,
                        index,
                      ) =>
                        renderTableRow(
                          row,
                          officeColumns,
                          index,
                          `office-${
                            row.uid ||
                            index
                          }`,
                        ),
                    )
                  ) : (
                    renderEmptyRow(
                      officeColumns.length,
                      "No office user activity found",
                    )
                  )}

                </tbody>

              </Table>

            </div>

            {renderPaginationBar({
              pagination:
                officePagination,

              page:
                officePage,

              limit:
                officeLimit,

              onPageChange:
                handleOfficePageChange,

              onLimitChange:
                handleOfficeLimitChange,
            })}

          </div>

        </Card.Body>

      </Card>

    </div>
  );
};

export default OverallSummaryReportPage;
